import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flashbar,
  Grid,
  Header,
  Modal,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import {
  ActivePackage,
  GetActivePackagesCommand,
  GetActivePackagesCommandOutput,
  GetUserGroupsCommand,
  GetUserGroupsCommandOutput,
  ListVenuesCommand,
  ListVenuesCommandOutput,
  RefreshAllActivePackagesCommand,
  RefreshAllActivePackagesCommandOutput,
  RefreshDiffsAgainstPublicCommand,
  RefreshDiffsAgainstPublicCommandOutput,
  VenueInfoItem,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import { ApiErrorProvider, useApiErrors } from '../components/ApiErrorContext';
import { ApiErrorNotifications } from '../components/ApiErrorNotifications';
import { callApi } from '../client';
import { ActivePackagesListTable } from './ActivePackagesListTable';
import { AskFusionAssistantButton } from '../fusionAssistant/AskFusionAssistantButton';
import { paginateAll, PAGE_SIZE } from '../utils/pagination';
import { venueDisplayName as venueDisplayNameFor } from '../utils/venueDisplay';
import {
  useConferenceVenueScope,
  ALL_CONFERENCES,
  ALL_VENUES,
} from '../hooks/useConferenceVenueScope';
import { useLatestRequest } from '../hooks/useLatestRequest';

function ActivePackagesListContent() {
  const [venues, setVenues] = useState<VenueInfoItem[]>([]);
  const [packages, setPackages] = useState<ActivePackage[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [confirmRegenVisible, setConfirmRegenVisible] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenBanner, setRegenBanner] = useState<string | null>(null);
  const [recomputingDiffs, setRecomputingDiffs] = useState(false);
  const [recomputeDiffsBanner, setRecomputeDiffsBanner] = useState<{
    type: 'success' | 'warning' | 'info';
    content: string;
  } | null>(null);
  const [confirmRecomputeDiffsVisible, setConfirmRecomputeDiffsVisible] =
    useState(false);

  const { isAuthenticated, token } = useAuthState();
  const { pushError } = useApiErrors();

  // Shared conference/venue scope state + derived Select options (D6). Replaces the per-page copies
  // of the sentinels, the conference/venue useState, and the conferences/venuesForConference memos.
  const scope = useConferenceVenueScope(venues);
  // Latest-request-wins guard: narrowing scope while a broad "All accessible" walk is in flight must
  // not let the slow broad result overwrite the newer narrow one (finding on CR-301380769).
  const packagesRequest = useLatestRequest();

  useEffect(() => {
    if (isAuthenticated && token) {
      void fetchVenues();
      void fetchAdminPermission();
    }
  }, [isAuthenticated, token]);

  // Default scope is "All accessible" (D6): no auto-select of a single conference. Fetch packages
  // whenever the scope (conference/venue) changes; an ALL_CONFERENCES scope sends no conference so
  // the backend returns every conference the caller can read.
  useEffect(() => {
    if (token) {
      void fetchPackages(scope.scopedConference, scope.scopedVenue);
    }
  }, [scope.conference, scope.venue, token]);

  async function fetchVenues() {
    if (!token) return;
    setVenuesLoading(true);
    try {
      const response: ListVenuesCommandOutput = await callApi(
        new ListVenuesCommand({}),
        token
      );
      if (response.$metadata.httpStatusCode === 200) {
        setVenues(response.venues ?? []);
      } else {
        pushError('Failed to load venues', 'Unexpected response from server');
      }
    } catch (err: any) {
      pushError('Failed to load venues', err?.message || String(err));
    } finally {
      setVenuesLoading(false);
    }
  }

  // Determine whether the caller may re-generate the table. active-packages-admin
  // is granted only to launch admins and matches the server-side gate on
  // RefreshAllActivePackages, so a non-admin never sees the button.
  async function fetchAdminPermission() {
    if (!token) return;
    try {
      const response: GetUserGroupsCommandOutput = await callApi(
        new GetUserGroupsCommand({}),
        token
      );
      if (response.$metadata.httpStatusCode === 200) {
        setIsAdmin(
          (response.permissions ?? []).includes('active-packages-admin')
        );
      }
    } catch (err: any) {
      // Non-fatal: without the flag the admin button simply stays hidden.
      console.error('Failed to fetch user permissions:', err);
    }
  }

  async function regenerateTable() {
    if (!token) return;
    setRegenerating(true);
    try {
      const response: RefreshAllActivePackagesCommandOutput = await callApi(
        new RefreshAllActivePackagesCommand({}),
        token
      );
      if (response.$metadata.httpStatusCode === 200) {
        const s = response.summary;
        setRegenBanner(
          `Regenerated the Active packages table: ${
            s?.rowsUpserted ?? 0
          } rows upserted, ${s?.rowsDeleted ?? 0} removed, ${
            s?.entitiesScanned ?? 0
          } entities scanned.`
        );
        // Reflect the rebuilt table in the current view.
        void fetchPackages(scope.scopedConference, scope.scopedVenue);
      } else {
        pushError('Regenerate failed', 'Unexpected response from server');
      }
    } catch (err: any) {
      pushError('Regenerate failed', err?.message || String(err));
    } finally {
      setRegenerating(false);
      setConfirmRegenVisible(false);
    }
  }

  async function recomputeDiffs() {
    if (!token) return;
    setRecomputingDiffs(true);
    try {
      const response: RefreshDiffsAgainstPublicCommandOutput = await callApi(
        new RefreshDiffsAgainstPublicCommand({}),
        token,
        { noRetry: true }
      );
      if (response.$metadata.httpStatusCode === 200) {
        const { rowsScanned, rowsUpdated, retryableFailures } =
          response.summary;
        if (retryableFailures > 0) {
          setRecomputeDiffsBanner({
            type: 'warning',
            content: `Recomputed diffs against AWSDevDocs/public: ${rowsUpdated} of ${rowsScanned} rows updated. ${retryableFailures} could not be checked due to a temporary error — re-run to retry them.`,
          });
        } else {
          setRecomputeDiffsBanner({
            type: 'success',
            content: `Recomputed diffs against AWSDevDocs/public: ${rowsUpdated} of ${rowsScanned} rows updated.`,
          });
        }
        // Reflect the refreshed diff columns in the current view.
        void fetchPackages(scope.scopedConference, scope.scopedVenue);
      } else {
        pushError('Recompute diffs failed', 'Unexpected response from server');
      }
    } catch (err: any) {
      // A large refresh can outrun API Gateway's 29s timeout (504) while the backend keeps
      // running and still writes the diff columns — surface it as "still running", not a failure.
      const status = err?.statusCode ?? err?.$metadata?.httpStatusCode;
      if (status === 504) {
        setRecomputeDiffsBanner({
          type: 'info',
          content:
            'Refresh is still running in the background — large conferences can take a few ' +
            'minutes. Reload this page shortly to see the updated Public branch, Public ' +
            'commit, Ancestry, and Diff columns.',
        });
      } else {
        pushError('Recompute diffs failed', err?.message || String(err));
      }
    } finally {
      setRecomputingDiffs(false);
      setConfirmRecomputeDiffsVisible(false);
    }
  }

  async function fetchPackages(conf?: string, ven?: string) {
    if (!token) return;
    try {
      await packagesRequest.run(
        setPackagesLoading,
        () =>
          paginateAll<ActivePackage>((nextToken) =>
            callApi(
              new GetActivePackagesCommand({
                conference: conf,
                venue: ven,
                pageSize: PAGE_SIZE,
                nextToken,
              }),
              token
            ).then((response: GetActivePackagesCommandOutput) => ({
              items: response.activePackages ?? [],
              nextToken: response.nextToken,
              statusCode: response.$metadata.httpStatusCode ?? 0,
            }))
          ),
        ({ items, ok, truncated }) => {
          // Only commit the list if every page succeeded AND the result wasn't truncated at the page
          // cap. A non-200 page or a leftover nextToken (more pages remain than the cap allows) must
          // surface an error rather than silently committing a partial list (CR-301380769 rev2). The
          // superseded-fetch drop and the loading-flag reset are owned by run() (CR-301380769 rev5).
          if (!ok || truncated) {
            pushError(
              'Failed to load active packages',
              ok
                ? 'Too many pages of results to load; please narrow the conference/venue filter.'
                : 'Unexpected response from server'
            );
            return;
          }
          setPackages(items);
        }
      );
    } catch (err: any) {
      setPackages([]);
      pushError('Failed to load active packages', err?.message || String(err));
    }
  }

  const scopeDescription =
    scope.conference === ALL_CONFERENCES
      ? 'Packages currently entered in any conference you can access'
      : `Packages currently entered in ${scope.conference}${
          scope.venue === ALL_VENUES ? ' (all venues)' : ` — ${scope.venue}`
        }`;

  // Display-only venue name for the table column + text filter: coordinator canonical override from
  // the venue list, else the raw venue (D2). Same helper the Launches/Groups tables use.
  const venueDisplayName = (conf?: string, ven?: string): string =>
    venueDisplayNameFor(venues, conf, ven);

  const refresh = () => {
    void fetchPackages(scope.scopedConference, scope.scopedVenue);
  };

  return (
    <SpaceBetween size='l'>
      {regenBanner && (
        <Flashbar
          items={[
            {
              type: 'success',
              content: regenBanner,
              dismissible: true,
              onDismiss: () => setRegenBanner(null),
              id: 'regenerate-banner',
            },
          ]}
        />
      )}
      {recomputeDiffsBanner && (
        <Flashbar
          items={[
            {
              type: recomputeDiffsBanner.type,
              content: recomputeDiffsBanner.content,
              dismissible: true,
              onDismiss: () => setRecomputeDiffsBanner(null),
              id: 'recompute-diffs-banner',
            },
          ]}
        />
      )}
      <Container
        header={
          <Header
            variant='h1'
            description='Every package currently entered into an active launch task or group, with its FLC build info and which tasks/groups reference it.'
            actions={<AskFusionAssistantButton />}
          >
            Active packages
          </Header>
        }
      >
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <Box>
            <Box variant='awsui-key-label'>Conference</Box>
            <Select
              selectedOption={scope.selectedConferenceOption}
              onChange={({ detail }) =>
                scope.selectConference(detail.selectedOption.value)
              }
              options={scope.conferenceOptions}
              placeholder='All accessible'
              loadingText='Loading conferences...'
              statusType={venuesLoading ? 'loading' : 'finished'}
              empty='No conferences available'
            />
          </Box>
          <Box>
            <Box variant='awsui-key-label'>Venue</Box>
            <Select
              selectedOption={scope.selectedVenueOption}
              onChange={({ detail }) =>
                scope.setVenue(detail.selectedOption.value ?? ALL_VENUES)
              }
              options={scope.venueOptions}
              disabled={scope.conference === ALL_CONFERENCES}
              placeholder='All venues'
            />
          </Box>
        </Grid>
      </Container>

      <ActivePackagesListTable
        data={packages}
        loading={packagesLoading}
        onRefresh={refresh}
        scopeDescription={scopeDescription}
        venueDisplayName={venueDisplayName}
        isAdmin={isAdmin}
        onRegenerate={() => setConfirmRegenVisible(true)}
        regenerating={regenerating}
        onRecomputeDiffs={() => setConfirmRecomputeDiffsVisible(true)}
        recomputingDiffs={recomputingDiffs}
      />

      <Modal
        visible={confirmRegenVisible}
        onDismiss={() => setConfirmRegenVisible(false)}
        header='Regenerate Active packages table?'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => setConfirmRegenVisible(false)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                loading={regenerating}
                onClick={regenerateTable}
                data-test-id='confirm-regenerate-button'
              >
                Regenerate
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        This rebuilds the entire Active packages table by re-scanning all launch
        tasks and groups, repairing any drift. It is safe and idempotent, but
        scans every active entity. Proceed?
      </Modal>

      <Modal
        visible={confirmRecomputeDiffsVisible}
        onDismiss={() => setConfirmRecomputeDiffsVisible(false)}
        header='Recompute diffs against Public?'
        footer={
          <Box float='right'>
            <SpaceBetween direction='horizontal' size='xs'>
              <Button
                variant='link'
                onClick={() => setConfirmRecomputeDiffsVisible(false)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                loading={recomputingDiffs}
                onClick={recomputeDiffs}
                data-test-id='confirm-recompute-diffs-button'
              >
                Recompute
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Recompute how every package in this table compares to AWSDevDocs/public?
        This updates the Public branch, Public commit, Ancestry, and Diff
        columns for every package.
      </Modal>
    </SpaceBetween>
  );
}

export function ActivePackagesList() {
  return (
    <ApiErrorProvider>
      <div>
        <ApiErrorNotifications />
        <ActivePackagesListContent />
      </div>
    </ApiErrorProvider>
  );
}

export default ActivePackagesList;
